import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Lightbox from 'react-images';
import Slider from 'react-slick';
import { decode } from 'html-decoder';
import { toast } from 'react-toastify';
import { disableBodyScroll } from 'body-scroll-lock';
import ReactSVG from 'react-svg';
import chevron from 'chevron-small.svg';
import upload from 'upload.svg';
import heart_path from 'heart_path.svg';
import { CloseLightbox } from '../InstagramSlider';
import FavoriteSuccessBanner from '../CustomToast/FavoriteSuccessBanner';
import useWindowSize from '../../hooks/useWindowSize';
import { handleHeartAnimation } from '../../utils/handleHeartAnimation';
import ErrorBoundary from '../ErrorBoundary';

const SliderImage = ({ src, onClick, ...props }) => (
  <div className="image-outer">
    {/* eslint-disable-next-line */}
    <img
      data-src={src}
      onClick={onClick}
      title="Property Image"
      className="lazyload"
      {...props}
    />
  </div>
);

function HeroSlider({
  images,
  property,
  property_city,
  community,
  property_favorited,
  current_user,
}) {
  const isSSR = typeof window === 'undefined';

  const heartRef = useRef(null);

  const [showLightbox, toggleLightbox] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [favorited, setFavorited] = useState(false);

  const { width: windowWidth } = useWindowSize();

  const [favoriteRoute, setFavoriteRoute] = useState(null);
  useEffect(() => {
    setFavoriteRoute(window.Routes.toggle_favorite_property_path(property));
  }, [property]);

  useEffect(() => {
    if (property_favorited) setFavorited(true);
  }, [property_favorited]);

  const handleFavorited = () => {
    const tokenElement = document.querySelector('[name=csrf-token]');
    if (current_user) {
      handleHeartAnimation({ ref: heartRef, isFavorited: favorited });
      axios
        .patch(favoriteRoute, null, {
          headers: {
            'X-CSRF-Token': tokenElement ? tokenElement.content : '',
            Accept: 'application/json',
          },
        })
        .then(res => {
          if (res.status === 200) {
            toast.dismiss();
            if (!favorited)
              toast(<FavoriteSuccessBanner favorited type={property} />);
            else toast(<FavoriteSuccessBanner type={property} />);
            setFavorited(!favorited);
          }
        })
        .catch(err => {
          console.error(`%c ERROR: ${err}`, 'font-size: 28px');
          setFavorited(favorited);
          const event = new CustomEvent('toast-message', { detail: `${err}` });
          document.dispatchEvent(event);
          if (err.response.status === 401) {
            window.location.pathname = window.Routes.login_path();
          }
        });
    } else {
      const modal = document.querySelector('#popup-modal');
      modal.classList.remove('fadeOut');
      modal.classList.remove('hidden');
      modal.classList.add('fadeIn');
      disableBodyScroll(modal);
    }
  };

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    variableWidth: true,
    arrows: false,
  };

  const slug = community.url.split('/').splice(-2, 1);
  return (
    <aside className="hero-slider full-page-width no-padding">
      {windowWidth > 768 ? (
        <ul className="page-content-breadcrumb rentals">
          <li>
            <a aria-label="Home Page" href={window.Routes.root_path()}>
              Home
            </a>
          </li>
          <ReactSVG src={chevron} />
          <li>
            <a
              aria-label={`${property_city} Page`}
              href={window.Routes.listings_area_type_path({
                area: property.city_slug,
              })}
            >
              {property_city}
            </a>
          </li>
          <ReactSVG src={chevron} />
          <li>
            <a
              aria-label={`${community.name} Page`}
              href={`/${property.city_slug}/vacation-rentals/${slug}`}
            >
              {decode(community.name)}
            </a>
          </li>
          <ReactSVG src={chevron} />
          <li>
            <a
              aria-label={`${property.name} Show Page`}
              href={property.property_url}
            >
              {decode(property.name)}
            </a>
          </li>
        </ul>
      ) : (
        <div className="property-slider-actions">
          <button
            aria-label="Share Property"
            name="Share Property"
            type="button"
            className="property-action"
            onClick={() => {
              document
                .querySelector('#share_modal_container')
                .classList.remove('hidden');
              disableBodyScroll(document.querySelector('#popup-modal'));
            }}
          >
            <ReactSVG src={upload} />
          </button>
          <button
            aria-label="Toggle Favorite"
            name="Toggle Favorite"
            type="button"
            ref={heartRef}
            className={`card-heart property-action ${
              favorited ? 'favorited' : ''
            }`}
            onClick={handleFavorited}
          >
            <ReactSVG src={heart_path} className="heart-icon" />
          </button>
        </div>
      )}
      {false ? null : (
        <>
          {windowWidth > 425 ? (
            <Fragment>
              {/* <button
            aria-label="View Property Photos"
            name="View Property Photos"
            type="button"
            onClick={() => {
              setImageIndex(0);
              toggleLightbox(true);
            }}
            className="btn btn--dark hero-view-images"
          >
            View Photos
          </button> */}
              <div className="slider-overlay-gradient" />
              <div className="slider-overflow">
                <SliderImage
                  alt={images[0].table.caption || `${decode(property.name)}_1`}
                  title={
                    images[0].table.caption || `${decode(property.name)}_1`
                  }
                  src={images[0].table.url}
                  onClick={() => {
                    setImageIndex(0);
                    toggleLightbox(true);
                  }}
                />
                {images.slice(1).map((image, index) => (
                  <SliderImage
                    key={index}
                    alt={
                      image.table.caption || `${decode(property.name)}_${index}`
                    }
                    title={
                      image.table.caption || `${decode(property.name)}_${index}`
                    }
                    src={image.table.url}
                    onClick={() => {
                      setImageIndex(index + 1);
                      toggleLightbox(true);
                    }}
                  />
                ))}
              </div>
            </Fragment>
          ) : (
            <>
              {!isSSR && (
                <Slider {...settings}>
                  {images.map((image, index) => (
                    <SliderImage
                      key={index}
                      alt={
                        image.table.caption ||
                        `${decode(property.name)}_${index}`
                      }
                      title={
                        image.table.caption ||
                        `${decode(property.name)}_${index}`
                      }
                      src={image.table.url}
                      onClick={() => {
                        setImageIndex(index + 1);
                        toggleLightbox(true);
                      }}
                    />
                  ))}
                </Slider>
              )}
            </>
          )}
          <Lightbox
            onClose={() => toggleLightbox(false)}
            onClickNext={() => setImageIndex(imageIndex + 1)}
            onClickPrev={() => setImageIndex(imageIndex - 1)}
            currentImage={imageIndex}
            isOpen={showLightbox}
            images={images.map(image => ({
              src: image.table.url,
              caption: (
                <section className="lightbox-caption">
                  <span>{decode(image.table.caption)}</span>
                </section>
              ),
            }))}
            onClickThumbnail={e => setImageIndex(e)}
            customControls={[
              <CloseLightbox onClick={() => toggleLightbox(false)} />,
            ]}
            closeButtonTitle="closeLightbox"
            leftArrowTitle="property_leftLightboxArrow"
            rightArrowTitle="property_rightLightboxArrow"
            showImageCount={false}
            showThumbnails
          />
        </>
      )}
    </aside>
  );
}

HeroSlider.propTypes = {
  images: PropTypes.array.isRequired,
  property: PropTypes.object.isRequired,
  property_city: PropTypes.string,
  community: PropTypes.object,
  current_user: PropTypes.object,
  property_favorited: PropTypes.bool,
};

SliderImage.propTypes = {
  src: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function HeroSliderWrapper({ ...props }) {
  return (
    <ErrorBoundary>
      <HeroSlider {...props} />
    </ErrorBoundary>
  );
}
